import { addDays, DAY_MS } from './dates';

export const ACTIVITY_TYPES = [
    { value: 'break/rest', label: 'Odmor' },
    { value: 'work', label: 'Ostali rad' },
    { value: 'driving', label: 'Vožnja' },
    { value: 'availability', label: 'Raspoloživost' },
    { value: 'unknown', label: 'Nepoznato' },
];

export const activityLabel = (type) => {
    const t = ACTIVITY_TYPES.find((x) => x.value === type);
    if (!t) throw new Error(`Tip aktivnosti '${type}' nije podržan`);
    return t.label;
};

export const OUT_TYPES = { 1: 'Početak', 2: 'Kraj' };

const midnight = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
};

let tmpId = 0;
/** Novi detalj aktivnosti (id 0 = još nije sačuvan; _key služi samo za React). */
export const newDetail = (activityType, startTime, endTime) => ({
    id: 0,
    _key: `n${++tmpId}`,
    activityType,
    startTime,
    endTime,
    totalTime: endTime - startTime,
});

/** Pravilo normalizacije: kraj 00:00 istog dana -> +1 dan; kraj različitog dana a nije 00:00 -> -1 dan. */
export const normalizeDetail = (d) => {
    const start = new Date(d.startTime);
    let end = new Date(d.endTime);
    const sameDay = start.toDateString() === end.toDateString();
    const endIsMidnight = end.getHours() === 0 && end.getMinutes() === 0;
    if (sameDay && endIsMidnight) end = addDays(end, 1);
    else if (!sameDay && !endIsMidnight) end = addDays(end, -1);
    return { ...d, startTime: start, endTime: end, totalTime: end - start };
};

export const sortDetails = (list) => [...list].sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

/** Brisanje više redova: izabrani redovi postaju odmor, a odmori koji se nastavljaju jedan na drugi se spajaju u jedan
    (dan ostaje pun 24 h, bez rupa). Spojen red zadržava id prvog, ostali nestaju (na čuvanju se brišu). */
export const uOdmor = (list, kljucevi, keyOf, { oznaci = false } = {}) => {
    /* oznaci: redovi nose izvor (kartica / listić / ručno) — obrisan red postaje ručni odmor, a spajaju se samo odmori istog izvora,
       nikad red sa kartice (zaključan) */
    const kljuc = (d) => `${d.izvor || 'RUCNO'}|${d.listicId || ''}`;
    const kartica = (d) => (d.izvor === 'KARTICA' || d.izvor === 'VOZILO') && d.activityType !== 'unknown';
    const out = [];
    sortDetails(list.map((d) => (kljucevi.has(keyOf(d))
        ? { ...d, activityType: 'break/rest', ...(oznaci ? { izvor: 'RUCNO', listicId: null, listic: null } : {}) } : d))).forEach((d) => {
        const p = out[out.length - 1];
        if (p && p.activityType === 'break/rest' && d.activityType === 'break/rest'
            && (!oznaci || (kljuc(p) === kljuc(d) && !kartica(p) && !kartica(d)))
            && new Date(p.endTime).getTime() === new Date(d.startTime).getTime()) {
            out[out.length - 1] = normalizeDetail({ ...p, endTime: d.endTime });
        } else out.push(d);
    });
    return out;
};

/** Početno vreme nove aktivnosti = kraj aktivnosti koja se najkasnije završava. */
export const nextStartTime = (list, day) => {
    if (!list.length) return midnight(day);
    return new Date(Math.max(...list.map((d) => new Date(d.endTime).getTime())));
};

/** "Dopuni": popuni sve rupe tokom dana aktivnostima tipa odmor. */
export const fillActivitiesWithBreak = (list, day) => {
    const dayStart = midnight(day);
    const dayEnd = addDays(dayStart, 1);
    if (!list.length) return [newDetail('break/rest', dayStart, dayEnd)];
    const sorted = sortDetails(list).map((d) => ({ ...d, startTime: new Date(d.startTime), endTime: new Date(d.endTime) }));
    if (sorted.every((d) => d.activityType === 'break/rest')) return [newDetail('break/rest', dayStart, dayEnd)];

    const out = [];
    const first = sorted[0];
    if (first.startTime > dayStart) {
        if (first.activityType === 'break/rest') first.startTime = dayStart;
        else out.push(newDetail('break/rest', dayStart, first.startTime));
    }
    sorted.forEach((cur, i) => {
        const prev = out[out.length - 1];
        if (prev && prev.endTime < cur.startTime) {
            /* kao stari TachoUtils: svaka rupa dobija zaseban red odmora */
            out.push(newDetail('break/rest', prev.endTime, cur.startTime));
        }
        out.push(cur);
    });
    const last = out[out.length - 1];
    if (last.endTime < dayEnd) {
        if (last.activityType === 'break/rest') last.endTime = dayEnd;
        else out.push(newDetail('break/rest', last.endTime, dayEnd));
    }
    return out.map((d) => ({ ...d, totalTime: d.endTime - d.startTime }));
};

/** Odmor u toku radnog vremena: suma odmora između prve i poslednje ne-odmor aktivnosti. */
export const workDayBreak = (details) => {
    const sorted = sortDetails(details || []);
    const work = sorted.filter((d) => d.activityType !== 'break/rest');
    /* kao stari activities.utils: bez radnih aktivnosti sabiraju se svi odmori dana */
    if (!work.length) return sorted.filter((d) => d.activityType === 'break/rest').reduce((s, d) => s + (new Date(d.endTime) - new Date(d.startTime)), 0);
    const from = new Date(work[0].startTime).getTime();
    const to = new Date(work[work.length - 1].endTime).getTime();
    return sorted
        .filter((d) => d.activityType === 'break/rest' && new Date(d.startTime).getTime() >= from && new Date(d.endTime).getTime() <= to)
        .reduce((s, d) => s + (new Date(d.endTime) - new Date(d.startTime)), 0);
};

export const sumDurations = (daily) =>
    (daily.totalBreakTime || 0) + (daily.totalDrivingTime || 0) + (daily.totalWorkTime || 0) +
    (daily.totalAvailabilityTime || 0) + (daily.totalUnknownTime || 0);

export const isFullDay = (daily) => sumDurations(daily) === DAY_MS;

export const emptyDaily = (day) => ({
    id: 0,
    activityDate: midnight(day),
    traversedDistance: 0,
    totalBreakTime: 0, totalDrivingTime: 0, totalNightDrivingTime: 0, totalWorkTime: 0,
    totalNightWorkTime: 0, totalAvailabilityTime: 0, totalNightAvailabilityTime: 0, totalUnknownTime: 0,
    note: null,
    activityDetails: [],
});

/** Pomeri detalje dana na drugi datum (isto vreme u danu) — posle promene datuma dana. */
export const shiftDetails = (details, fromDay, toDay) => {
    if (!fromDay || !toDay) return details;
    const diff = Math.round((midnight(toDay) - midnight(fromDay)) / 86400000);
    if (!diff) return details;
    return details.map((d) => ({ ...d, startTime: addDays(d.startTime, diff), endTime: addDays(d.endTime, diff) }));
};

/** Prvi slobodan datum počev od zadatog (zauzeti = niz Date). */
export const firstAvailableDate = (date, busy) => {
    const taken = new Set((busy || []).map((d) => midnight(d).getTime()));
    let x = midnight(date);
    while (taken.has(x.getTime())) x = addDays(x, 1);
    return x;
};

/** Oznaka vozača u dropdown-ima. */
export const driverLabel = (d) =>
    `${d.lastName} ${d.firstName} - ${d.uniqueId}${d.company ? ` - ${d.company.name}` : ''}`;
